import { gql, useQuery } from "urql";
import { useAuth } from "../auth/AuthContext";

const GetMyWorkplacesDocument = gql`
  query GetMyWorkplacesByUserId($userId: UUID!) {
    userById(id: $userId) {
      workplaceUsersByUserId {
        nodes {
          id
          userId
          workplaceId
          role
          name
          order
          workplaceByWorkplaceId {
            id
            name
            hexColor
            order
          }
        }
      }
    }
  }
`;

export type MyWorkplace = {
  workplaceUserId: string;
  workplaceId: string;
  userId: string | null;
  role: string;
  name: string;
  order: number;
  workplace: {
    id: string;
    name: string;
    hexColor: string;
    order: number;
  };
};

type GetMyWorkplacesQuery = {
  userById?: {
    workplaceUsersByUserId: {
      nodes: Array<{
        id: string;
        userId: string | null;
        workplaceId: string;
        role: string;
        name: string;
        order: number;
        workplaceByWorkplaceId?: {
          id: string;
          name: string;
          hexColor: string;
          order: number;
        } | null;
      }>;
    };
  } | null;
};

const lastWorkplaceStorageKey = "shiftas.lastWorkplaceId";

export function getLastWorkplaceId() {
  return window.localStorage.getItem(lastWorkplaceStorageKey);
}

export function setLastWorkplaceId(workplaceId: string) {
  window.localStorage.setItem(lastWorkplaceStorageKey, workplaceId);
}

export function clearLastWorkplaceId() {
  window.localStorage.removeItem(lastWorkplaceStorageKey);
}

export function useMyWorkplaces() {
  const { userId } = useAuth();
  const [result] = useQuery<GetMyWorkplacesQuery>({
    query: GetMyWorkplacesDocument,
    variables: { userId },
    pause: !userId,
    requestPolicy: "cache-and-network",
  });

  const workplaces: MyWorkplace[] =
    result.data?.userById?.workplaceUsersByUserId.nodes
      .map((node) => {
        const workplace = node.workplaceByWorkplaceId;
        if (!workplace) return null;
        return {
          workplaceUserId: node.id,
          workplaceId: node.workplaceId,
          userId: node.userId,
          role: node.role,
          name: node.name,
          order: node.order,
          workplace,
        };
      })
      .filter((workplace): workplace is MyWorkplace => workplace !== null)
      .sort((a, b) => a.workplace.order - b.workplace.order) ?? [];

  return {
    ...result,
    workplaces,
  };
}
